import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Holiday, 
  getHolidaysForCountryAsync, 
  isCountrySupported,
  preloadHolidays,
  getHolidaysForCountry
} from '@/lib/integrations/HolidayService';

interface UseHolidaysProps {
  countryCode: string | null;
  year?: number;
  enabled?: boolean;
  strategy?: 'async' | 'simple';
}

interface UseHolidaysReturn {
  holidays: Holiday[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isHoliday: (date: Date) => Holiday | null;
}

export function useHolidays({ 
  countryCode, 
  year = new Date().getFullYear(),
  enabled = true,
  strategy = 'async'
}: UseHolidaysProps): UseHolidaysReturn {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHolidays = useCallback(async () => {
    if (!enabled || !countryCode || !isCountrySupported(countryCode)) {
      setHolidays([]);
      setError(countryCode && !isCountrySupported(countryCode) 
        ? `Country ${countryCode} is not supported for holiday detection` 
        : null
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fetchedHolidays = strategy === 'async' 
        ? await getHolidaysForCountryAsync(countryCode, year)
        : getHolidaysForCountry(countryCode, year);
      
      setHolidays(fetchedHolidays);
      
      // Preload adjacent years for better performance (only for async strategy)
      if (strategy === 'async') {
        preloadHolidays(countryCode, [year - 1, year + 1]).catch(console.warn);
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch holidays';
      setError(errorMessage);
      console.error('Error fetching holidays:', err);
    } finally {
      setLoading(false);
    }
  }, [countryCode, year, enabled, strategy]);

  const refresh = useCallback(async () => {
    await fetchHolidays();
  }, [fetchHolidays]);

  const isHoliday = useCallback((date: Date): Holiday | null => {
    // Use local timezone to avoid day shifts
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return holidays.find(h => h.date === dateStr) || null;
  }, [holidays]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  return {
    holidays,
    loading,
    error,
    refresh,
    isHoliday
  };
}

// Hook for multi-year holidays (useful for calendar views spanning multiple years)
interface UseMultiYearHolidaysProps {
  countryCode: string | null;
  years: number[];
  enabled?: boolean;
  strategy?: 'async' | 'simple';
}

interface UseMultiYearHolidaysReturn {
  holidaysByYear: Record<number, Holiday[]>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isHoliday: (date: Date) => Holiday | null;
  getHolidaysForMonth: (date: Date) => Holiday[];
}

export function useMultiYearHolidays({ 
  countryCode, 
  years,
  enabled = true,
  strategy = 'async'
}: UseMultiYearHolidaysProps): UseMultiYearHolidaysReturn {
  const [holidaysByYear, setHolidaysByYear] = useState<Record<number, Holiday[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize years array to prevent infinite loops
  const memoizedYears = useMemo(() => years, [years.join(',')]);

  const fetchAllHolidays = useCallback(async () => {
    if (!enabled || !countryCode || !isCountrySupported(countryCode) || memoizedYears.length === 0) {
      setHolidaysByYear({});
      setError(countryCode && !isCountrySupported(countryCode) 
        ? `Country ${countryCode} is not supported for holiday detection` 
        : null
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let newHolidaysByYear: Record<number, Holiday[]>;
      
      if (strategy === 'async') {
        const holidayPromises = memoizedYears.map(year => 
          getHolidaysForCountryAsync(countryCode, year).then(holidays => ({ year, holidays }))
        );
        
        const results = await Promise.all(holidayPromises);
        
        newHolidaysByYear = {};
        results.forEach(({ year, holidays }) => {
          newHolidaysByYear[year] = holidays;
        });
      } else {
        const holidayPromises = memoizedYears.map(year => 
          Promise.resolve(getHolidaysForCountry(countryCode, year)).then(holidays => ({ year, holidays }))
        );
        
        const results = await Promise.all(holidayPromises);
        
        newHolidaysByYear = {};
        results.forEach(({ year, holidays }) => {
          newHolidaysByYear[year] = holidays;
        });
      }
      
      setHolidaysByYear(newHolidaysByYear);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch holidays';
      setError(errorMessage);
      console.error('Error fetching multi-year holidays:', err);
    } finally {
      setLoading(false);
    }
  }, [countryCode, memoizedYears, enabled, strategy]);

  const refresh = useCallback(async () => {
    await fetchAllHolidays();
  }, [fetchAllHolidays]);

  const isHoliday = useCallback((date: Date): Holiday | null => {
    const year = date.getFullYear();
    // Use local timezone to avoid day shifts
    const dateStr = `${year}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const yearHolidays = holidaysByYear[year] || [];
    return yearHolidays.find(h => h.date === dateStr) || null;
  }, [holidaysByYear]);

  const getHolidaysForMonth = useCallback((date: Date): Holiday[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const yearHolidays = holidaysByYear[year] || [];
    
    return yearHolidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate.getFullYear() === year && holidayDate.getMonth() === month;
    });
  }, [holidaysByYear]);

  useEffect(() => {
    fetchAllHolidays();
  }, [fetchAllHolidays]);

  return {
    holidaysByYear,
    loading,
    error,
    refresh,
    isHoliday,
    getHolidaysForMonth
  };
}

// ===== UNIFIED HOLIDAY HOOK =====

interface UseHolidayDataProps {
  countryCode: string | null;
  years?: number[];
  enabled?: boolean;
}

interface UseHolidayDataReturn {
  holidays: Holiday[];
  holidaysByYear: Record<number, Holiday[]>;
  isHoliday: (date: Date) => Holiday | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useHolidayData({ 
  countryCode, 
  years,
  enabled = true
}: UseHolidayDataProps): UseHolidayDataReturn {
  const currentYear = new Date().getFullYear();
  
  // Memoize defaultYears to prevent recreating array on every render
  const defaultYears = useMemo(() => [currentYear - 1, currentYear, currentYear + 1], [currentYear]);
  const finalYears = years || defaultYears;

  // Use the existing multi-year hook with async strategy to properly fetch from Supabase
  const { 
    holidaysByYear, 
    loading, 
    error, 
    refresh,
    isHoliday: checkIsHoliday 
  } = useMultiYearHolidays({
    countryCode,
    years: finalYears,
    enabled,
    strategy: 'async'
  });

  // Flatten all holidays for convenience
  const holidays = useMemo(() => {
    return Object.values(holidaysByYear).flat();
  }, [holidaysByYear]);

  return {
    holidays,
    holidaysByYear,
    isHoliday: checkIsHoliday,
    loading,
    error,
    refresh
  };
}

// Backward compatibility exports
export const useHolidaysSimple = (props: Omit<UseHolidaysProps, 'strategy'>) => 
  useHolidays({ ...props, strategy: 'simple' });

export const useMultiYearHolidaysSimple = (props: Omit<UseMultiYearHolidaysProps, 'strategy'>) => 
  useMultiYearHolidays({ ...props, strategy: 'simple' });